import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  InputAdornment,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import { AdminLayout } from "../../components/admin/AdminLayout";
import {
  useAlbums,
  useCreateAlbum,
  useUpdateAlbum,
  useDeleteAlbum,
} from "../../hooks/useAlbums";
import { GalleryForm } from "../../components/admin/GalleryForm";
import { DeleteConfirmationDialog } from "../../components/admin/DeleteConfirmationDialog";
import { GalleryListItem } from "../../components/admin/GalleryListItem";
import type { AlbumResponse, AlbumCreate, AlbumUpdate } from "../../api/generated/types.gen";

// Alias for convenience
type Album = AlbumResponse;

export const GalleryManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingAlbum, setEditingAlbum] = useState<Album | null>(null);
  const [deletingAlbum, setDeletingAlbum] = useState<Album | null>(null);

  const { data: albumsData, isLoading } = useAlbums({ page_size: 100 });
  const createAlbumMutation = useCreateAlbum();
  const updateAlbumMutation = useUpdateAlbum();
  const deleteAlbumMutation = useDeleteAlbum();

  const albums = albumsData?.items || [];

  const filteredAlbums = albums.filter(
    (album) =>
      album.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (album.description || "")
        .toLowerCase()
        .includes(searchQuery.toLowerCase())
  );

  const handleCreate = () => {
    setEditingAlbum(null);
    setFormOpen(true);
  };

  const handleEdit = (album: Album) => {
    // Navigate to the edit page instead of opening modal
    navigate(`/admin/galleries/${album.id}/edit`);
  };

  const handleDelete = (album: Album) => {
    setDeletingAlbum(album);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (deletingAlbum) {
      try {
        await deleteAlbumMutation.mutateAsync(deletingAlbum.id);
        setDeleteDialogOpen(false);
        setDeletingAlbum(null);
      } catch (error) {
        // Error is handled by the mutation
      }
    }
  };

  const handleFormSubmit = async (data: AlbumCreate | AlbumUpdate) => {
    try {
      if (editingAlbum) {
        await updateAlbumMutation.mutateAsync({
          albumId: editingAlbum.id,
          data: data as AlbumUpdate,
        });
      } else {
        await createAlbumMutation.mutateAsync(data as AlbumCreate);
      }
      setFormOpen(false);
      setEditingAlbum(null);
    } catch (error) {
      // Error is handled by the mutation
    }
  };

  const handleView = (album: Album) => {
    window.open(`/gallery/${album.slug}`, "_blank");
  };

  if (isLoading) {
    return (
      <AdminLayout>
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <Typography>Loading galleries...</Typography>
        </Box>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <Box>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", sm: "center" },
            mb: { xs: 3, sm: 4 },
            gap: { xs: 2, sm: 0 },
          }}
        >
          <Box>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 600,
                mb: 1,
                fontSize: { xs: "1.5rem", sm: "2rem" },
              }}
            >
              Gallery Management
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "text.secondary",
                fontSize: { xs: "0.875rem", sm: "1rem" },
              }}
            >
              Manage your photography galleries
            </Typography>
          </Box>
          <Button
            variant="text"
            startIcon={<AddIcon />}
            onClick={handleCreate}
            sx={{
              textTransform: "none",
              fontSize: { xs: "0.875rem", sm: "1rem" },
              px: { xs: 2, sm: 2 },
              py: { xs: 1.25, sm: 1 },
              minHeight: { xs: "44px", sm: "40px" },
              width: { xs: "100%", sm: "auto" },
              fontWeight: 500,
              border: "none",
              boxShadow: "none",
            }}
          >
            New Gallery
          </Button>
        </Box>

        {/* Search */}
        <Box sx={{ mb: { xs: 2, sm: 3 } }}>
          <TextField
            fullWidth
            placeholder="Search galleries..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            variant="standard"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{
              backgroundColor: "#fff",
              "& .MuiInputBase-root": {
                fontSize: { xs: "16px", sm: "16px" }, // Prevent zoom on iOS
                minHeight: { xs: "48px", sm: "40px" }, // Better touch target on mobile
              },
              "& .MuiInputBase-input": {
                padding: { xs: "12px 14px", sm: "10px 14px" },
              },
              "& .MuiInput-underline:before": {
                borderBottom: "none",
              },
              "& .MuiInput-underline:hover:before": {
                borderBottom: "none",
              },
              "& .MuiInput-underline:after": {
                borderBottom: "none",
              },
            }}
          />
        </Box>

        {/* Gallery List */}
        {filteredAlbums.length === 0 ? (
          <Paper
            sx={{
              p: 4,
              textAlign: "center",
              backgroundColor: "#fff",
              borderRadius: 0,
            }}
          >
            <Typography variant="body1" sx={{ color: "text.secondary" }}>
              No galleries found
            </Typography>
          </Paper>
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
              gap: { xs: 1, sm: 1.5 },
            }}
          >
            {filteredAlbums.map((album) => (
              <GalleryListItem
                key={album.id}
                album={album}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onView={handleView}
              />
            ))}
          </Box>
        )}

        {/* Summary */}
        <Box sx={{ mt: { xs: 2, sm: 3 } }}>
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              fontSize: { xs: "0.875rem", sm: "0.875rem" },
            }}
          >
            Showing {filteredAlbums.length} of {albums.length} galleries
          </Typography>
        </Box>
      </Box>

      {/* Gallery Form Modal */}
      <GalleryForm
        open={formOpen}
        onClose={() => {
          setFormOpen(false);
          setEditingAlbum(null);
        }}
        onSubmit={handleFormSubmit}
        album={editingAlbum}
        isLoading={
          createAlbumMutation.isPending || updateAlbumMutation.isPending
        }
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setDeletingAlbum(null);
        }}
        onConfirm={handleConfirmDelete}
        title="Delete Gallery"
        message={
          deletingAlbum
            ? `Are you sure you want to delete "${deletingAlbum.title}"? This action cannot be undone.`
            : ""
        }
        isLoading={deleteAlbumMutation.isPending}
      />
    </AdminLayout>
  );
};
